<template>
    <div class="amEditor">
        <header class="amEditor__toolbar">
            <input
                class="amEditor__title"
                placeholder="Enter an animation title"
                :value="animationNew.label"
                @blur="onTitleInput"
            >

            <div class="amEditor__dropdown">
                <label for="animation-repeat" class="amEditor__dropdown-label">
                    Repeat
                </label>

                <dropdown
                    id="animation-repeat"
                    class="amEditor__dropdown-select"
                    :options="repeatOptions"
                    :value="repeat"
                    @input="onRepeatInput"
                />
            </div>

            <div class="amEditor__dropdown">
                <label for="animation-repeat-delay" class="amEditor__dropdown-label">
                    Repeat Delay
                </label>

                <dropdown
                    id="animation-repeat-delay"
                    class="amEditor__dropdown-select"
                    :options="repeatDelayOptions"
                    :value="repeatDelay"
                    @input="onRepeatDelayInput"
                />
            </div>

            <div class="amEditor__dropdown">
                <label for="animation-repeat-delay" class="amEditor__dropdown-label">
                    Repeat Style
                </label>

                <dropdown
                    id="animation-repeat-delay"
                    class="amEditor__dropdown-select"
                    :options="repeatAlternateOptions"
                    :value="repeatAlternate"
                    @input="onRepeatAlternateInput"
                />
            </div>
        </header>

        <div class="amEditor__content" :style="containerStyle">
            <animation-steps :tweens="animationNew.tweens" @input="onInput" />
            <animation-preview :is-active="mode === modes.PREVIEW" :animation="animation" />
        </div>

        <footer class="amEditor__footer">
            <button type="button" class="amEditor__preview" @click="onTogglePreview">
                {{ previewButtonText }}
            </button>

            <button type="button" class="amEditor__back" @click="onViewLibrary">
                Back to Library
            </button>
        </footer>
    </div>
</template>

<script>
import AnimationSteps from '@/components/settings/component/animations/animation-steps';
import AnimationPreview from '@/components/settings/component/animations/animation-preview';

const modes = {
    EDIT: 'edit',
    PREVIEW: 'preview',
};

export default {
    name: 'animation-editor',
    components: { AnimationSteps, AnimationPreview },

    props: {
        animation: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            animationNew: {},
            mode: modes.EDIT,
            modes,
            repeat: 0,
            repeatDelay: 0,
            repeatAlternate: false,

            repeatOptions: [
                { text: 'None', value: 0 },
                { text: '1x', value: 1 },
                { text: '2x', value: 2 },
                { text: '3x', value: 3 },
                { text: '4x', value: 4 },
                { text: '5x', value: 5 },
                { text: 'Infinite', value: -1 },
            ],

            repeatDelayOptions: [
                { text: 'None', value: 0 },
                { text: '0.25 Seconds', value: 0.25 },
                { text: '0.5 Seconds', value: 0.5 },
                { text: '0.75 Seconds', value: 0.75 },
                { text: '1 Second', value: 1 },
                { text: '2 Seconds', value: 2 },
                { text: '3 Seconds', value: 3 },
                { text: '4 Seconds', value: 4 },
                { text: '5 Seconds', value: 5 },
            ],

            repeatAlternateOptions: [
                { text: 'Default', value: false },
                { text: 'Yo-Yo', value: true },
            ],
        };
    },

    computed: {
        containerStyle() {
            let leftPos;

            switch (this.mode) {
                case modes.EDIT:
                    leftPos = '0%';
                    break;

                case modes.PREVIEW:
                    leftPos = '-100%';
                    break;

                default:
                    // do nothing
            }

            return { left: leftPos };
        },

        previewButtonText() {
            return this.mode === modes.EDIT ? 'Preview' : 'Edit';
        },
    },

    watch: {
        animation: {
            handler() {
                this.animationNew = this.animation;
            },
            deep: true,
        },
    },

    methods: {
        onTitleInput(e) {
            this.animationNew.label = e.target.value;
            this.onInput();
        },

        onRepeatInput(value) {
            this.animationNew.repeat = value;
            this.onInput();
        },

        onRepeatDelayInput(value) {
            this.animationNew.repeatDelay = value;
            this.onInput();
        },

        onRepeatAlternateInput(value) {
            this.animationNew.repeatAlternate = value;
            this.onInput();
        },

        onTogglePreview() {
            this.mode = this.mode === modes.PREVIEW ? modes.EDIT : modes.PREVIEW;
        },

        onViewLibrary() {
            this.mode = modes.EDIT;
            this.$emit('back');
        },

        onInput() {
            this.$emit('input', this.animationNew);
        },
    },
};
</script>

<style lang="scss">
.amEditor {
    &__toolbar {
        display: flex;
    }

    &__title {
        @extend %input-display;
        margin-right: auto;
        min-width: 16em;
        align-self: flex-end;
        padding: 0.5em 0.75em;
    }

    &__dropdown {
        &:not(:last-of-type) {
            margin-right: 2em;
        }
    }

    &__dropdown-label {
        display: block;
        font-size: 0.875rem;
        margin-bottom: 0.25em;
    }

    &__dropdown-select {
        min-width: 14em;
    }

    &__container {
        display: flex;
    }

    &__content {
        position: relative;
        display: flex;
        margin: 3em 0;
        transition: left 0.25s;
    }

    &__footer {
        display: flex;
    }

    &__preview {
        @extend %button;
        font-weight: 600;

        &:not(:first-of-type) {
            margin-left: 2em;
        }
    }

    &__back {
        @extend %button-alt;
        font-weight: 600;
        margin-left: auto;
    }
}
</style>
